/* <xall-catalog> — the Shop All toolbar: one category filter axis, four
   sorts, applied chips, a unique-product count. The behavior contract is the
   design project's notes/collections-filter-sort-proposal.md, ported in
   docs/design-maps/shop-all.md:

   - filter = pruning (Apply/Clear draft in the panel; chips + Clear all in
     the white row under the fold); the Bestsellers shelf is merchandising,
     not taxonomy, so it hides whenever a filter is applied and is never a
     filter option;
   - Featured keeps the sectioned page; any other sort flattens the home
     shelves into one sorted grid (Bestsellers cells never enter it — they
     are duplicates), strict order, out-of-stock not demoted;
   - filter options, counts and badges derive from the shelf sections in the
     DOM — nothing is typed here.

   The shelves are sibling Shopify sections, so this element orchestrates
   them by hook ([data-x-shelf]) rather than by containment. Controls stay
   hidden until it connects: without JS the page is the full sectioned,
   anchorable catalog with nothing dead on screen. */
(() => {
  if (customElements.get('xall-catalog')) return;

  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
  const CHECK = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 13l4 4L19 7"/></svg>';
  const X = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>';

  class XallCatalog extends HTMLElement {
    connectedCallback() {
      if (this.dataset.xInit) return;
      this.dataset.xInit = '1';
      this.shelves = Array.from(document.querySelectorAll('[data-x-shelf]')).map((el) => ({
        el,
        id: el.getAttribute('data-x-shelf'),
        label: el.getAttribute('data-x-label') || '',
        badge: el.getAttribute('data-x-badge') || '',
        merch: el.hasAttribute('data-x-merch'),
        cells: Array.from(el.querySelectorAll('[data-x-cell]')).map((cell) => ({
          cell,
          home: cell.parentNode,
          pid: cell.getAttribute('data-x-pid'),
          price: parseInt(cell.getAttribute('data-x-price'), 10) || 0,
          rating: parseFloat(cell.getAttribute('data-x-rating')) || 0,
        })),
      }));
      if (!this.shelves.length) return;
      this.flatBand = document.querySelector('.x-cflat');
      this.flatGrid = document.querySelector('[data-x-flat]');
      this.chipsBand = document.querySelector('.x-cchips');
      this.chipsHost = document.querySelector('[data-x-chips]');
      this.countEl = this.querySelector('[data-x-count]');
      this.fcountEl = this.querySelector('[data-x-fcount]');
      this.sortLabelEl = this.querySelector('[data-x-sort-label]');
      this.applied = [];
      this.draft = [];
      this.sort = 'featured';
      this.buildFilter();
      this.wire();
      this.render();
      this.classList.add('x-on');
    }

    filterable() { return this.shelves.filter((s) => !s.merch && s.label); }

    buildFilter() {
      const host = this.querySelector('[data-x-opts]');
      if (!host) return;
      host.innerHTML = this.filterable().map((s) => {
        const n = new Set(s.cells.map((c) => c.pid)).size;
        const badge = s.badge
          ? ' <span class="x-badge x-badge--gold x-badge--xs">' + esc(s.badge) + '</span>'
          : '';
        return '<label class="x-opt">'
          + '<input type="checkbox" value="' + esc(s.id) + '">'
          + '<span class="x-bx">' + CHECK + '</span>'
          + '<span class="x-lb">' + esc(s.label) + badge + '</span>'
          + '<i>' + n + '</i>'
          + '</label>';
      }).join('');
    }

    wire() {
      this.querySelectorAll('.x-tdd').forEach((dd) => {
        const btn = dd.querySelector('[data-x-toggle]');
        if (btn) btn.addEventListener('click', () => this.toggle(dd, !dd.classList.contains('x-on')));
        const scrim = dd.querySelector('.x-scrim');
        if (scrim) scrim.addEventListener('click', () => this.toggle(dd, false));
      });
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const open = this.querySelector('.x-tdd.x-on');
        if (open) { this.toggle(open, false); open.querySelector('[data-x-toggle]').focus(); }
      });
      const opts = this.querySelector('[data-x-opts]');
      if (opts) opts.addEventListener('change', () => {
        this.draft = Array.from(opts.querySelectorAll('input:checked')).map((i) => i.value);
        this.syncPanel();
      });
      const clear = this.querySelector('[data-x-clear]');
      if (clear) clear.addEventListener('click', () => { this.draft = []; this.syncPanel(); });
      const apply = this.querySelector('[data-x-apply]');
      if (apply) apply.addEventListener('click', () => {
        this.applied = this.draft.slice();
        this.toggle(this.querySelector('[data-x-dd="filter"]'), false);
        this.render();
      });
      this.querySelectorAll('[data-x-sort]').forEach((b) => {
        b.addEventListener('click', () => {
          this.sort = b.getAttribute('data-x-sort');
          this.querySelectorAll('[data-x-sort]').forEach((o) => o.setAttribute('aria-checked', o === b ? 'true' : 'false'));
          if (this.sortLabelEl) this.sortLabelEl.textContent = b.textContent.trim();
          this.toggle(this.querySelector('[data-x-dd="sort"]'), false);
          this.render();
        });
      });
      if (this.chipsHost) this.chipsHost.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-x-chip]');
        if (chip) {
          this.applied = this.applied.filter((id) => id !== chip.getAttribute('data-x-chip'));
          this.render();
          return;
        }
        if (e.target.closest('[data-x-clear-all]')) { this.applied = []; this.render(); }
      });
    }

    toggle(dd, open) {
      if (!dd) return;
      this.querySelectorAll('.x-tdd').forEach((d) => {
        const on = open && d === dd;
        d.classList.toggle('x-on', on);
        d.querySelector('[data-x-toggle]').setAttribute('aria-expanded', on ? 'true' : 'false');
      });
      if (open && dd.getAttribute('data-x-dd') === 'filter') {
        this.draft = this.applied.slice();
        this.syncPanel();
      }
    }

    syncPanel() {
      const opts = this.querySelector('[data-x-opts]');
      if (opts) opts.querySelectorAll('input').forEach((i) => { i.checked = this.draft.includes(i.value); });
      const clear = this.querySelector('[data-x-clear]');
      if (clear) clear.disabled = !this.draft.length;
    }

    render() {
      const filtered = this.applied.length > 0;
      const flat = this.sort !== 'featured';

      // 1. every cell goes home first (append in captured order rebuilds it)
      this.shelves.forEach((s) => s.cells.forEach((c) => {
        if (c.cell.parentNode !== c.home) c.home.appendChild(c.cell);
      }));

      // 2. shelf visibility
      this.shelves.forEach((s) => {
        const show = !flat && (s.merch ? !filtered : (!filtered || this.applied.includes(s.id)));
        s.el.hidden = !show;
      });

      // 3. flat grid — home-shelf cells only, filter still applies
      let visible;
      if (flat) {
        const pool = this.filterable()
          .filter((s) => !filtered || this.applied.includes(s.id))
          .flatMap((s) => s.cells);
        const cmp = this.sort === 'rated' ? (a, b) => b.rating - a.rating
          : this.sort === 'price-asc' ? (a, b) => a.price - b.price
            : (a, b) => b.price - a.price;
        pool.slice().sort(cmp).forEach((c) => this.flatGrid.appendChild(c.cell));
        visible = pool;
      } else {
        visible = this.shelves.filter((s) => !s.el.hidden).flatMap((s) => s.cells);
      }
      if (this.flatBand) this.flatBand.classList.toggle('x-has', flat);

      // 4. chips
      if (this.chipsHost) {
        this.chipsHost.innerHTML = !filtered ? '' : this.applied.map((id) => {
          const s = this.shelves.find((x) => x.id === id);
          return '<button type="button" class="x-chip" data-x-chip="' + esc(id) + '">'
            + esc(s ? s.label : id) + X + '</button>';
        }).join('')
          + '<button type="button" class="x-ca" data-x-clear-all>'
          + esc(this.dataset.xClearAllLabel || 'Clear all') + '</button>';
        if (this.chipsBand) this.chipsBand.classList.toggle('x-has', filtered);
      }

      // 5. counts — unique products among what is on screen
      if (this.countEl) {
        const n = new Set(visible.map((c) => c.pid)).size;
        this.countEl.textContent = n + ' ' + (this.dataset.xCountLabel || 'products');
      }
      if (this.fcountEl) {
        this.fcountEl.textContent = this.applied.length || '';
        this.fcountEl.hidden = !filtered;
      }
    }
  }

  customElements.define('xall-catalog', XallCatalog);
})();
